// src/pages/Dashboard/Admin/Articles/ArticlesDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useArticlesDashboard } from "./hooks/useArticlesDashboard";

import DashboardArticlesHeader from "./components/DashboardArticlesHeader";
import ArticlesKpiCards from "./components/ArticlesKpiCards";
import ArticlesFiltersCard from "./components/ArticlesFiltersCard";
import ArticlesCatalogTable from "./components/ArticlesCatalogTable";
import ArticleFormDrawer from "./components/ArticleFormDrawer";
import ArticleImageDrawer from "./components/ArticleImageDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedArticlesPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Articles boutique
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ArticlesDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = useArticlesDashboard();

  if (roleId !== 1) {
    return <UnauthorizedArticlesPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardArticlesHeader onCreate={dashboard.openCreateDrawer} />

        <ArticlesKpiCards
          stats={dashboard.stats}
          filteredStats={dashboard.filteredStats}
          loading={dashboard.loading}
        />

        <ArticlesFiltersCard
          filters={dashboard.filters}
          setFilter={dashboard.setFilter}
          resetFilters={dashboard.resetFilters}
          categoryOptions={dashboard.categoryOptions}
          filteredCount={dashboard.filteredArticles.length}
          totalCount={dashboard.articles.length}
          canReorder={dashboard.canReorder}
          loading={dashboard.loading}
          onRefresh={dashboard.fetchArticles}
        />

        <ArticlesCatalogTable
          articles={dashboard.filteredArticles}
          loading={dashboard.loading}
          canReorder={dashboard.canReorder}
          draggingId={dashboard.draggingId}
          dragOverId={dashboard.dragOverId}
          deletingId={dashboard.deletingId}
          togglingId={dashboard.togglingId}
          reordering={dashboard.reordering}
          onDragStart={dashboard.handleDragStart}
          onDragOver={dashboard.handleDragOver}
          onDragLeave={dashboard.handleDragLeave}
          onDrop={dashboard.handleDrop}
          onMoveUp={(article) => dashboard.moveArticle(article.id, "up")}
          onMoveDown={(article) => dashboard.moveArticle(article.id, "down")}
          onToggleActive={dashboard.toggleActiveStatus}
          onEdit={dashboard.openEditDrawer}
          onDelete={dashboard.handleDeleteArticle}
          onImage={dashboard.openImageDrawer}
        />
      </div>

      <ArticleFormDrawer
        isOpen={dashboard.formDrawerOpen}
        mode={dashboard.formMode}
        article={dashboard.editableArticle}
        categories={dashboard.categoryOptions}
        nextOrder={dashboard.nextOrder}
        submitting={dashboard.submittingForm}
        onClose={dashboard.closeFormDrawer}
        onSubmit={
          dashboard.formMode === "create"
            ? dashboard.handleCreateArticle
            : dashboard.handleUpdateArticle
        }
      />

      <ArticleImageDrawer
        isOpen={dashboard.imageDrawerOpen}
        article={dashboard.imageArticle}
        uploading={dashboard.uploadingImage}
        onClose={dashboard.closeImageDrawer}
        onUpload={dashboard.handleUploadImage}
      />

      <ToastContainer />
    </section>
  );
}